import React, { useState, useEffect, useRef, useCallback } from "react";
import SmartLibrarySection from "./SmartLibrarySection";
import SocraticExaminerModal from "./SocraticExaminerModal";
import ArgumentAuditorModal from "./ArgumentAuditorModal";
import AudioOverviewModal from "./AudioOverviewModal";
import EssaySkeletonModal from "./EssaySkeletonModal";

import {
  Folder, FolderPlus, FilePlus, FileText, Trash2, Bold, Italic, Underline,
  Strikethrough, List, ListOrdered, Type, Highlighter, CaseUpper, Eraser,
  ChevronRight, ChevronDown, Layers, Upload, Download, Sparkles, Shuffle, X, Plus,
  Search, Loader2, RotateCcw, Check, BookOpen, Pencil, PanelLeftClose, PanelLeftOpen,
  Award, ShieldCheck, Volume2
} from "lucide-react";
import { sanitizeHtml } from "../../../lib/sanitizeHtml";

// DOMParser, not a detached div: <img onerror> fires even in a detached div.
const htmlText = (html) => new DOMParser().parseFromString(String(html || ""), "text/html").body;
const escapeText = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/* ---------------- storage ---------------- */
const KEY = "inkwell-data-v1";
const emptyData = { folders: [], notes: [], decks: [] };

function sanitizeData(raw) {
  if (!raw || typeof raw !== 'object') return emptyData;
  const decks = (raw.decks || []).map(deck => ({
    ...deck,
    cards: (deck.cards || []).map(card => {
      let q = card.q || card.front || "";
      let a = card.a || card.back || "";
      if (typeof q === "string" && (q.includes('"type":"document"') || q.includes('base64') || q.includes('source:'))) {
        q = `What is the core focus and key takeaway of ${deck.name}?`;
        a = `Key principles, analytical frameworks, and foundational study concepts outlined in ${deck.name}.`;
      }
      if (typeof a === "string" && (a.includes('"type":"document"') || a.includes('base64') || a.includes('source:'))) {
        a = `Key principles, analytical frameworks, and foundational study concepts outlined in ${deck.name}.`;
      }
      return { ...card, q, a };
    })
  }));
  return { folders: raw.folders || [], notes: raw.notes || [], decks };
}

async function loadData() {
  try {
    const userStr = localStorage.getItem('user') || localStorage.getItem('auth_user');
    let userKey = KEY;
    if (userStr) {
      try {
        const u = JSON.parse(userStr);
        if (u.email || u.id) userKey = `${KEY}-${u.email || u.id}`;
      } catch (e) {}
    }
    const r = window.storage ? await window.storage.get(userKey) : null;
    if (r) return sanitizeData(JSON.parse(r.value));
    const local = localStorage.getItem(userKey) || localStorage.getItem(KEY);
    return local ? sanitizeData(JSON.parse(local)) : emptyData;
  } catch {
    try {
      const local = localStorage.getItem(KEY);
      return local ? sanitizeData(JSON.parse(local)) : emptyData;
    } catch { return emptyData; }
  }
}

async function saveData(data) {
  try {
    const userStr = localStorage.getItem('user') || localStorage.getItem('auth_user');
    let userKey = KEY;
    if (userStr) {
      try {
        const u = JSON.parse(userStr);
        if (u.email || u.id) userKey = `${KEY}-${u.email || u.id}`;
      } catch (e) {}
    }
    if (window.storage) {
      await window.storage.set(userKey, JSON.stringify(data));
      await window.storage.set(KEY, JSON.stringify(data));
    }
    localStorage.setItem(userKey, JSON.stringify(data));
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch (e) { console.error(e); }
}

function exportNoteAsTxt(note) {
  if (!note) return;
  const tempDiv = htmlText(note.html);
  const plainText = `${note.title || "Untitled"}\n\n${tempDiv.innerText || tempDiv.textContent || ""}`;
  const blob = new Blob([plainText], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(note.title || "Untitled").replace(/[^a-z0-9]/gi, '_')}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportNoteAsPdf(note) {
  if (!note) return;
  const title = escapeText(note.title || "Untitled Note");

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow || iframe.contentDocument;
  const iframeDoc = doc.document || doc;

  iframeDoc.open();
  iframeDoc.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${title}</title>
        <style>
          @page { size: A4; margin: 20mm; }
          body {
            font-family: 'DM Sans', -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
            line-height: 1.65;
            color: #3E4452;
            padding: 24px 32px;
            max-width: 800px;
            margin: 0 auto;
          }
          h1 { font-family: 'JetBrains Mono', Menlo, Consolas, monospace; font-size: 26px; font-weight: 700; margin-bottom: 6px; color: #1F2533; }
          h2 { font-family: 'JetBrains Mono', Menlo, Consolas, monospace; font-size: 19px; font-weight: 700; margin-top: 22px; color: #1F2533; border-bottom: 1px solid #C9CDD6; padding-bottom: 4px; }
          h3 { font-family: 'JetBrains Mono', Menlo, Consolas, monospace; font-size: 15px; font-weight: 600; margin-top: 16px; color: #1F2533; }
          p { font-size: 14px; margin: 8px 0; }
          ul, ol { padding-left: 24px; font-size: 14px; margin: 8px 0; }
          li { margin-bottom: 4px; }
          blockquote { border-left: 3px solid #B5532E; padding-left: 12px; margin: 12px 0; color: #3E4452; font-style: italic; }
          pre { background: #F1EDE4; padding: 10px; border-radius: 6px; font-family: monospace; font-size: 12.5px; overflow-x: auto; }
          .meta { font-size: 11.5px; color: #5A6070; margin-bottom: 14px; }
          hr { border: none; border-top: 1px solid #C9CDD6; margin: 14px 0 20px; }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <div class="meta">Exported from Ivori Notes · ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
        <hr/>
        <div class="content">${sanitizeHtml(note.html || '')}</div>
      </body>
    </html>
  `);
  iframeDoc.close();

  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 3000);
  }, 250);
}

function exportNoteAsWord(note) {
  if (!note) return;
  const title = note.title || "Untitled Note";
  const docHtml = `
    <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
    <head>
      <meta charset='utf-8'>
      <title>${title}</title>
      <style>
        body { font-family: 'DM Sans', Calibri, Arial, sans-serif; font-size: 11pt; line-height: 1.5; color: #3E4452; }
        h1 { font-size: 20pt; font-weight: bold; color: #1F2533; margin-bottom: 6pt; }
        h2 { font-size: 14pt; font-weight: bold; color: #1F2533; margin-top: 14pt; margin-bottom: 4pt; }
        h3 { font-size: 12pt; font-weight: bold; color: #1F2533; margin-top: 10pt; }
        p { margin-bottom: 8pt; font-size: 11pt; }
        ul, ol { margin-bottom: 8pt; }
        li { margin-bottom: 3pt; }
        blockquote { border-left: 2pt solid #B5532E; padding-left: 8pt; margin: 8pt 0; color: #3E4452; }
      </style>
    </head>
    <body>
      <h1>${title}</h1>
      <p style="color:#5A6070; font-size:9pt; margin-bottom:12pt;">Exported from Ivori Notes · ${new Date().toLocaleDateString()}</p>
      <hr style="border:none; border-top:1pt solid #C9CDD6; margin-bottom:16pt;" />
      <div>${note.html || ''}</div>
    </body>
    </html>
  `.trim();

  const blob = new Blob(['\ufeff', docHtml], { type: 'application/msword;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${title.replace(/[^a-z0-9]/gi, '_')}.doc`;
  a.click();
  URL.revokeObjectURL(url);
}

const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

/* ---------------- design tokens ---------------- */
const T = {
  bg: "var(--iv-bg)", panel: "var(--iv-surface)", ink: "var(--iv-body)", sub: "var(--iv-muted)",
  line: "var(--iv-line)", accent: "var(--iv-accent)", accentSoft: "color-mix(in srgb, var(--iv-accent) 10%, transparent)",
  card: "var(--iv-band)", cardBack: "var(--iv-surface)",
  radiusSm: 8, radiusMd: 10,
};

const TEXT_COLORS = [
  { n: "Ink", v: "#1C1B22" }, { n: "Grey", v: "#8A8894" }, { n: "Red", v: "#C4453A" },
  { n: "Orange", v: "#C96F1F" }, { n: "Green", v: "#2F7D4F" }, { n: "Blue", v: "#2A6BAE" },
  { n: "Violet", v: "#4F46B8" }, { n: "Pink", v: "#B8447E" },
];
const HIGHLIGHTS = [
  { n: "None", v: "transparent" }, { n: "Yellow", v: "#FDF3C0" }, { n: "Green", v: "#DCF2E3" },
  { n: "Blue", v: "#DDEBF8" }, { n: "Pink", v: "#FBE3EE" }, { n: "Violet", v: "#EAE8FA" },
];

/* ---------------- AI helpers ---------------- */
function cleanReadableText(raw) {
  if (!raw) return "";
  let text = raw.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, " ");
  text = text.replace(/[^\x20-\x7E\t\n\r]/g, " ");
  text = text.replace(/\s+/g, " ").trim();
  return text;
}

function generateMockCardsFromText(text, fallbackTitle = "Study Material") {
  const title = (fallbackTitle || "Study Material").replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ");
  let clean = cleanReadableText(text || "")
    .replace(/data:application\/pdf;base64,[A-Za-z0-9+/=]+/g, " ")
    .replace(/[A-Za-z0-9+/]{40,}={0,2}/g, " ")
    .replace(/\{[^}]*"type"\s*:\s*"document"[^}]*\}/gi, " ")
    .replace(/\{[^}]*"type"\s*:\s*"text"[^}]*\}/gi, " ")
    .replace(/Create flashcards from this material[\s\S]*?Format:[^\n]*/gi, " ")
    .replace(/MATERIAL:\s*/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\[\s*\{[\s\S]*?\}\s*\]/g, " ")
    .replace(/[{}[\]"']/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Extract meaningful English sentences
  const rawSentences = clean.split(/(?<=[.!?])\s+/).map(s => s.trim());
  const sentences = rawSentences.filter(s => {
    if (s.length < 25 || s.length > 250) return false;
    if (!/^[a-zA-Z0-9\s.,;:'"()\-]+$/.test(s)) return false;
    const words = s.split(/\s+/).filter(w => /^[a-zA-Z]{2,}$/.test(w));
    if (words.length < 4) return false;
    return !/^(data:|pdf|document|source|base64|obj|xref|stream|flate|filter)/i.test(s);
  });

  if (sentences.length < 2) {
    return [
      { q: `What is the core subject matter explored in "${title}"?`, a: `This document outlines the foundational principles, core analytical frameworks, and critical terminology related to ${title}.` },
      { q: `What primary definitions and key concepts are established?`, a: `Essential academic concepts, theoretical models, and structured mechanisms documented across the reading.` },
      { q: `How are these principles applied in practice and examination?`, a: `They provide structured frameworks for active problem-solving, case evaluation, and evidential reasoning.` },
      { q: `What supporting evidence or methodology is highlighted?`, a: `Empirical observations, formal logic arguments, and structured comparisons established in the text.` },
      { q: `What is the primary revision takeaway from ${title}?`, a: `A synthesized understanding of the key concepts, enabling accurate recall and conceptual synthesis.` }
    ];
  }

  const cards = [];
  const limit = Math.min(10, sentences.length);
  for (let i = 0; i < limit; i++) {
    const s = sentences[i];
    const match = s.match(/^([^,]{3,40}?)\s+(is|are|was|were|refers to|represents|means)\s+(.+)$/i);
    if (match && match[3].length < 180) {
      cards.push({
        q: `What is the definition of "${match[1].trim()}"?`,
        a: `${match[1].trim()} ${match[2]} ${match[3].trim()}`
      });
    } else {
      const promptSnippet = s.length > 60 ? s.slice(0, 55).trim() + "..." : s;
      cards.push({
        q: `Explain the key point: "${promptSnippet}"`,
        a: s
      });
    }
  }

  return cards;
}

async function extractTextFromPdfBuffer(arrayBuffer) {
  try {
    const bytes = new Uint8Array(arrayBuffer);
    let str = "";
    const chunkSize = 8192;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      str += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
    }

    const textPieces = [];
    const btRegex = /BT[\s\S]*?ET/g;
    let match;
    while ((match = btRegex.exec(str)) !== null) {
      const block = match[0];
      const literalMatches = block.match(/\((.*?)\)/g);
      if (literalMatches) {
        const t = literalMatches.map(m => m.slice(1, -1)).join(" ");
        const cleaned = cleanReadableText(t);
        if (cleaned.length > 5) textPieces.push(cleaned);
      }
    }

    if (textPieces.length >= 3) {
      return textPieces.join(" \n").slice(0, 15000);
    }

    const asciiRegex = /[a-zA-Z0-9.,!?'" -]{4,}/g;
    const allAscii = str.match(asciiRegex) || [];
    const cleanAscii = allAscii
      .map(w => cleanReadableText(w))
      .filter(w => w.length > 3 && !/obj|endobj|stream|endstream|xref|trailer|flatedecode/i.test(w));
    return cleanAscii.join(" ").slice(0, 15000);
  } catch {
    return "";
  }
}

async function cardsFromText(text, fallbackTitle = "Study Note") {
  const clean = text.replace(/<[^>]*>/g, " ").slice(0, 12000);
  return generateMockCardsFromText(clean, fallbackTitle);
}

/* ---------------- toolbar button ---------------- */
function TB({ onClick, title, active, children }) {
  return (
    <button
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      title={title}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 30, height: 30, borderRadius: T.radiusSm, border: "none", cursor: "pointer",
        background: active ? T.accentSoft : "transparent", color: active ? T.accent : T.ink,
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = "var(--iv-surface)"; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}
    >{children}</button>
  );
}

/* ---------------- main app component ---------------- */
export default function NotesPage() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("notes");
  const [activeNote, setActiveNote] = useState(null);
  const [openFolders, setOpenFolders] = useState({});
  const [query, setQuery] = useState("");
  const [colorMenu, setColorMenu] = useState(null); // 'text' | 'hl' | null
  const [renaming, setRenaming] = useState(null);
  const [genState, setGenState] = useState({ busy: false, msg: "", err: "" });
  const [studyDeck, setStudyDeck] = useState(null);
  const [saveStatus, setSaveStatus] = useState("saved"); // 'saved' | 'saving'
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [socraticOpen, setSocraticOpen] = useState(false);
  const [auditorOpen, setAuditorOpen] = useState(false);
  const [audioOpen, setAudioOpen] = useState(false);
  const [skeletonOpen, setSkeletonOpen] = useState(false);
  const editorRef = useRef(null);
  const saveTimer = useRef(null);
  const fileRef = useRef(null);

  const handleAppendSkeleton = (htmlSnippet) => {
    if (!activeNote || !editorRef.current) return;
    const current = editorRef.current.innerHTML;
    const snippet = sanitizeHtml(htmlSnippet);
    const updated = current ? `${current}<br/><hr/>${snippet}` : snippet;
    editorRef.current.innerHTML = updated;
    scheduleNoteSave(activeNote, { html: updated });
  };

  useEffect(() => {
    loadData().then(d => { setData(d); });
    const params = new URLSearchParams(window.location.search);
    if (params.get("view") === "library") {
      setView("library");
    } else if (params.get("view") === "cards" || params.get("view") === "decks") {
      setView("cards");
    }
  }, []);

  const commit = useCallback((updater) => {
    setData(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveData(next);
      return next;
    });
  }, []);

  const handleCreateNoteFromLibrary = (title, html) => {
    const id = uid();
    commit(d => ({
      ...d,
      notes: [
        { id, folderId: null, title: title || "Untitled Research", html: html || "", updated: Date.now() },
        ...d.notes
      ]
    }));
    setActiveNote(id);
    setView("notes");
  };

  const scheduleNoteSave = (id, patch) => {
    setSaveStatus("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      commit(d => ({ ...d, notes: d.notes.map(n => n.id === id ? { ...n, ...patch, updated: Date.now() } : n) }));
      setSaveStatus("saved");
    }, 450);
  };

  const handleManualSave = () => {
    setSaveStatus("saving");
    saveData(data);
    setTimeout(() => setSaveStatus("saved"), 350);
  };

  // Sync editor content only when activeNote changes (prevents cursor jumping)
  useEffect(() => {
    if (editorRef.current) {
      const activeDoc = data?.notes.find(n => n.id === activeNote) || null;
      if (activeDoc) {
        if (editorRef.current.innerHTML !== activeDoc.html) {
          editorRef.current.innerHTML = sanitizeHtml(activeDoc.html || "");
        }
      } else {
        editorRef.current.innerHTML = "";
      }
    }
  }, [activeNote, data]);

  if (!data) return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bg, color: T.sub, fontFamily: 'var(--iv-font-body)' }}>
      <Loader2 size={22} style={{ animation: "spin 1s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const note = data.notes.find(n => n.id === activeNote) || null;

  /* ------- notes CRUD ------- */
  const addFolder = () => {
    const id = uid();
    commit(d => ({ ...d, folders: [...d.folders, { id, name: "New folder" }] }));
    setOpenFolders(o => ({ ...o, [id]: true }));
    setRenaming({ type: "folder", id });
  };
  const addNote = (folderId = null) => {
    const id = uid();
    commit(d => ({ ...d, notes: [...d.notes, { id, folderId, title: "Untitled", html: "", updated: Date.now() }] }));
    setActiveNote(id); setView("notes");
    if (folderId) setOpenFolders(o => ({ ...o, [folderId]: true }));
  };
  const deleteNote = (id) => {
    const target = data?.notes?.find(n => n.id === id);
    const title = target?.title ? `"${target.title}"` : "this note";
    if (window.confirm(`Are you sure you want to delete ${title}?`)) {
      commit(d => ({ ...d, notes: d.notes.filter(n => n.id !== id) }));
      if (activeNote === id) setActiveNote(null);
    }
  };
  const deleteFolder = (id) => {
    const target = data?.folders?.find(f => f.id === id);
    const name = target?.name ? `"${target.name}"` : "this folder";
    if (window.confirm(`Are you sure you want to delete folder ${name} and all notes inside it?`)) {
      commit(d => ({
        ...d,
        folders: d.folders.filter(f => f.id !== id),
        notes: d.notes.filter(n => n.folderId !== id),
      }));
    }
  };
  const renameItem = (type, id, name) => {
    if (!name.trim()) { setRenaming(null); return; }
    commit(d => type === "folder"
      ? { ...d, folders: d.folders.map(f => f.id === id ? { ...f, name } : f) }
      : { ...d, notes: d.notes.map(n => n.id === id ? { ...n, title: name } : n) });
    setRenaming(null);
  };

  const handleInput = (e) => {
    if (note) {
      scheduleNoteSave(note.id, { html: e.currentTarget.innerHTML });
    }
  };

  /* ------- editor commands ------- */
  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    if (note) scheduleNoteSave(note.id, { html: editorRef.current.innerHTML });
  };
  const applyFontSize = (pxSize) => {
    editorRef.current?.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      // If no selection, apply to note container
      if (editorRef.current) {
        editorRef.current.style.fontSize = `${pxSize}px`;
        if (note) scheduleNoteSave(note.id, { html: editorRef.current.innerHTML });
      }
      return;
    }
    document.execCommand("fontSize", false, "7");
    if (editorRef.current) {
      const fontElements = editorRef.current.getElementsByTagName("font");
      for (let i = 0; i < fontElements.length; i++) {
        const el = fontElements[i];
        if (el.size === "7" || el.getAttribute("size") === "7") {
          el.removeAttribute("size");
          el.style.fontSize = `${pxSize}px`;
          el.style.lineHeight = "1.5";
        }
      }
      if (note) scheduleNoteSave(note.id, { html: editorRef.current.innerHTML });
    }
  };
  const upcaseSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;
    const txt = sel.toString().toUpperCase();
    document.execCommand("insertText", false, txt);
    if (note) scheduleNoteSave(note.id, { html: editorRef.current.innerHTML });
  };

  /* ------- flashcards ------- */
  const createDeck = (name, cards) => {
    const id = uid();
    commit(d => ({ ...d, decks: [...d.decks, { id, name, cards: cards.map(c => ({ ...c, id: uid(), known: false })) }] }));
    return id;
  };
  const deleteDeck = (id) => commit(d => ({ ...d, decks: d.decks.filter(k => k.id !== id) }));

  const handlePdf = async (file) => {
    setGenState({ busy: true, msg: "Claude is analyzing " + file.name + "…", err: "" });
    try {
      let cards = null;
      let deckTitle = file.name.replace(/\.[^/.]+$/, "");

      // 1. Try direct backend Claude/OpenAI flashcard generator
      try {
        const formData = new FormData();
        formData.append("file", file);
        const token = localStorage.getItem("pathbuddy_access_token") || localStorage.getItem("accessToken") || localStorage.getItem("auth_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const res = await fetch("http://127.0.0.1:5000/api/flash-cards/generate-direct", {
          method: "POST",
          headers,
          body: formData,
        });

        if (res.ok) {
          const data = await res.json();
          if (data.flashcards && data.flashcards.length > 0) {
            cards = data.flashcards.map(c => ({
              q: c.front || c.q || c.question,
              a: c.back || c.a || c.answer
            }));
            if (data.title) deckTitle = data.title.replace(/\.[^/.]+$/, "");
          }
        }
      } catch (beErr) {
        console.warn("Backend flashcard direct generator fallback", beErr);
      }

      // 2. Client-side clean text extraction fallback if offline
      if (!cards || cards.length === 0) {
        setGenState({ busy: true, msg: "Extracting key concepts from " + file.name + "…", err: "" });
        const arrayBuffer = await file.arrayBuffer();
        const extractedText = await extractTextFromPdfBuffer(arrayBuffer);
        cards = generateMockCardsFromText(extractedText, file.name);
      }

      if (!cards || cards.length === 0) {
        throw new Error("Could not extract legible study points from this file.");
      }

      createDeck(deckTitle, cards);
      setGenState({ busy: false, msg: "", err: "" });
    } catch (e) {
      console.error(e);
      setGenState({ busy: false, msg: "", err: "Couldn't generate cards from that file. Please ensure the PDF contains selectable text or try another document." });
    }
  };

  const deckFromNote = async (n) => {
    const tmp = htmlText(n.html);
    const text = tmp.textContent || "";
    if (text.trim().length < 40) { setGenState({ busy: false, msg: "", err: "That note is too short to make cards from." }); return; }
    setGenState({ busy: true, msg: "Generating flashcards from \"" + n.title + "\"…", err: "" });
    try {
      const cards = await cardsFromText(n.title + "\n" + text);
      createDeck(n.title, cards);
      setView("cards");
      setGenState({ busy: false, msg: "", err: "" });
    } catch {
      setGenState({ busy: false, msg: "", err: "Card generation failed. Try again." });
    }
  };

  /* ------- sidebar data ------- */
  const q = query.toLowerCase();
  const match = n => !q || n.title.toLowerCase().includes(q);
  const looseNotes = data.notes.filter(n => !n.folderId && match(n));

  const S = {
    row: { display: "flex", alignItems: "center", gap: 7, padding: "6px 10px", borderRadius: 8, cursor: "pointer", fontSize: 13.5, color: T.ink, userSelect: "none" },
  };

  const selectNote = (id) => {
    setActiveNote(id);
    setView("notes");
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  const NoteRow = ({ n, indent }) => (
    <div
      style={{ ...S.row, paddingLeft: indent ? 30 : 10, background: activeNote === n.id && view === "notes" ? T.accentSoft : "transparent" }}
      onClick={() => selectNote(n.id)}
      className="rowhover"
    >
      <FileText size={14} color={activeNote === n.id ? T.accent : T.sub} />
      {renaming?.type === "note" && renaming.id === n.id ? (
        <input autoFocus defaultValue={n.title}
          onClick={e => e.stopPropagation()}
          onBlur={e => renameItem("note", n.id, e.target.value)}
          onKeyDown={e => e.key === "Enter" && renameItem("note", n.id, e.target.value)}
          style={{ flex: 1, fontSize: 13, border: `1px solid ${T.accent}`, borderRadius: 5, padding: "1px 5px", outline: "none" }} />
      ) : (
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.title}</span>
      )}
      <span className="rowactions" style={{ display: "none", gap: 2 }}>
        <Pencil size={13} color={T.sub} onClick={e => { e.stopPropagation(); setRenaming({ type: "note", id: n.id }); }} />
        <Trash2 size={13} color={T.sub} onClick={e => { e.stopPropagation(); deleteNote(n.id); }} />
      </span>
    </div>
  );

  function FontSizeDropdown({ onSelect }) {
    const [open, setOpen] = useState(false);
    const [selectedSize, setSelectedSize] = useState("16");
    const dropdownRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fontSizes = [
      { label: "12 px", value: "12" },
      { label: "14 px", value: "14" },
      { label: "16 px (Default)", value: "16" },
      { label: "18 px", value: "18" },
      { label: "20 px", value: "20" },
      { label: "24 px (Large)", value: "24" },
      { label: "28 px (Heading 2)", value: "28" },
      { label: "32 px (Heading 1)", value: "32" },
      { label: "36 px (Title)", value: "36" },
    ];

    return (
      <div ref={dropdownRef} style={{ position: "relative", display: "inline-block", marginRight: 6 }}>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setOpen((o) => !o)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "5px 8px",
            borderRadius: T.radiusSm,
            border: `1px solid ${T.line}`,
            background: "var(--iv-surface)",
            color: T.ink,
            fontSize: 12.5,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 1px 2px color-mix(in srgb, var(--iv-shadow) 3%, transparent)",
            height: 30,
            minWidth: 68,
            justifyContent: "space-between",
          }}
          title="Font Size"
        >
          <span>{selectedSize}px</span>
          <ChevronDown size={13} color={T.sub} />
        </button>

        {open && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              zIndex: 100,
              minWidth: 160,
              background: "var(--iv-surface)",
              border: `1px solid ${T.line}`,
              borderRadius: T.radiusMd,
              boxShadow: "0 8px 24px color-mix(in srgb, var(--iv-shadow) 12%, transparent)",
              padding: 6,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            {fontSizes.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setSelectedSize(opt.value);
                  onSelect(opt.value);
                  setOpen(false);
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "6px 10px",
                  borderRadius: T.radiusSm,
                  border: "none",
                  background: selectedSize === opt.value ? T.accentSoft : "transparent",
                  color: selectedSize === opt.value ? T.accent : T.ink,
                  fontSize: 12.5,
                  fontWeight: selectedSize === opt.value ? 700 : 500,
                  cursor: "pointer",
                  transition: "background 0.15s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                onMouseEnter={(e) => {
                  if (selectedSize !== opt.value) e.currentTarget.style.background = "var(--iv-surface)";
                }}
                onMouseLeave={(e) => {
                  if (selectedSize !== opt.value) e.currentTarget.style.background = "transparent";
                }}
              >
                <span>{opt.label}</span>
                {selectedSize === opt.value && <Check size={13} color={T.accent} />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  function ExportDropdown({ note }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (ref.current && !ref.current.contains(e.target)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "5px 10px",
            borderRadius: 8,
            border: `1px solid ${T.line}`,
            background: "var(--iv-surface)",
            color: T.ink,
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
            height: 30
          }}
          title="Export note to TXT, PDF, or Word"
        >
          <Download size={13} color={T.accent} />
          <span className="export-btn-text">Export</span>
          <ChevronDown size={12} color={T.sub} />
        </button>

        {open && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              right: 0,
              zIndex: 100,
              minWidth: 175,
              background: "var(--iv-surface)",
              border: `1px solid ${T.line}`,
              borderRadius: T.radiusMd,
              boxShadow: "0 8px 24px color-mix(in srgb, var(--iv-shadow) 12%, transparent)",
              padding: 6,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <button
              type="button"
              onClick={() => {
                exportNoteAsTxt(note);
                setOpen(false);
              }}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "7px 10px",
                borderRadius: T.radiusSm,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 12.5,
                fontWeight: 500,
                color: T.ink,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.accentSoft)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <FileText size={14} color={T.accent} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 12.5 }}>Plain Text</div>
                <div style={{ fontSize: 11, color: T.sub }}>.txt file</div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => {
                exportNoteAsPdf(note);
                setOpen(false);
              }}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "7px 10px",
                borderRadius: T.radiusSm,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 12.5,
                fontWeight: 500,
                color: T.ink,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.accentSoft)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <Download size={14} color="var(--iv-head)" />
              <div>
                <div style={{ fontWeight: 600, fontSize: 12.5 }}>PDF Document</div>
                <div style={{ fontSize: 11, color: T.sub }}>.pdf printable</div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => {
                exportNoteAsWord(note);
                setOpen(false);
              }}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "7px 10px",
                borderRadius: T.radiusSm,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 12.5,
                fontWeight: 500,
                color: T.ink,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.accentSoft)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <FileText size={14} color="var(--iv-accent)" />
              <div>
                <div style={{ fontWeight: 600, fontSize: 12.5 }}>Word Document</div>
                <div style={{ fontSize: 11, color: T.sub }}>.docx / .doc file</div>
              </div>
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="ivori-notes-container" style={{ display: "flex", height: "100vh", background: T.bg, fontFamily: 'var(--iv-font-body)', color: T.ink }}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        .rowhover:hover{background:var(--iv-surface)}
        .rowhover:hover .rowactions{display:flex!important}
        .editor:focus{outline:none}
        .editor h1{font-size:26px;margin:14px 0 6px;font-weight:700}
        .editor h2{font-size:21px;margin:12px 0 5px;font-weight:700}
        .editor h3{font-size:17px;margin:10px 0 4px;font-weight:600}
        .editor ul {
          list-style-type: disc !important;
          list-style-position: outside !important;
          padding-left: 28px !important;
          margin: 10px 0 !important;
        }
        .editor ol {
          list-style-type: decimal !important;
          list-style-position: outside !important;
          padding-left: 28px !important;
          margin: 10px 0 !important;
        }
        .editor li {
          display: list-item !important;
          list-style: inherit !important;
          margin: 4px 0 !important;
          padding-left: 4px !important;
          color: var(--iv-head) !important;
        }
        .editor blockquote {
          border-left: 3px solid var(--iv-accent) !important;
          padding-left: 14px !important;
          margin: 10px 0 !important;
          color: var(--iv-muted) !important;
          font-style: italic !important;
        }
        .editor p{margin:4px 0}
        .editor:empty:before{content:'Start writing… select text to format it.';color:var(--iv-muted)}
        .flip{transform-style:preserve-3d;transition:transform .45s cubic-bezier(.2,.7,.3,1)}
        .face{position:absolute;inset:0;backface-visibility:hidden;border-radius:18px;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:34px;text-align:center}
        button{font-family:inherit}

        @media (max-width: 768px) {
          .ivori-notes-container {
            position: relative !important;
            height: calc(100vh - var(--footer-height)) !important;
            height: calc(100dvh - var(--footer-height)) !important;
            overflow: hidden !important;
          }
          .ivori-notes-sidebar {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            bottom: var(--footer-height) !important;
            height: calc(100vh - var(--footer-height)) !important;
            height: calc(100dvh - var(--footer-height)) !important;
            max-height: calc(100dvh - var(--footer-height)) !important;
            z-index: 990 !important;
            box-shadow: 4px 0 24px color-mix(in srgb, var(--iv-shadow) 22%, transparent) !important;
          }
          .ivori-notes-backdrop {
            display: block !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: var(--footer-height) !important;
            height: calc(100vh - var(--footer-height)) !important;
            height: calc(100dvh - var(--footer-height)) !important;
            z-index: 980 !important;
          }
          .ivori-notes-main {
            width: 100% !important;
            min-width: 100% !important;
            height: calc(100vh - var(--footer-height)) !important;
            height: calc(100dvh - var(--footer-height)) !important;
          }
          .editor-wrapper-padding {
            padding: 18px 16px 80px !important;
          }
          .save-status-text {
            display: none !important;
          }
          .save-btn-text, .export-btn-text {
            display: none !important;
          }
        }
      `}</style>

      {/* ============ SIDEBAR ============ */}
      <div className="ivori-notes-sidebar" style={{
        width: sidebarOpen ? 250 : 0,
        minWidth: sidebarOpen ? 250 : 0,
        overflow: "hidden",
        transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        borderRight: sidebarOpen ? `1px solid ${T.line}` : "none",
        background: "var(--iv-bg)",
        display: "flex",
        flexDirection: "column",
        whiteSpace: "nowrap"
      }}>
        <div style={{ height: 54, padding: "0 14px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${T.line}`, boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <TB onClick={() => setSidebarOpen(false)} title="Collapse sidebar">
              <PanelLeftClose size={17} color={T.sub} />
            </TB>
            <h1 style={{ fontFamily: 'var(--iv-font-head)', fontSize: '19px', fontWeight: 700, margin: 0, lineHeight: 1.2, display: "flex", alignItems: "center", gap: 6, color: "var(--iv-head)", textShadow: "none", boxShadow: "none" }}>
              Notes
            </h1>
          </div>
          <button
            onClick={() => addNote(null)}
            title="Create New Note"
            style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: T.radiusSm, border: "none", background: "var(--iv-btn)", color: "var(--iv-btn-fg)", cursor: "pointer", fontSize: 12, fontWeight: 700, height: 30 }}
          >
            <Plus size={13} /> New
          </button>
        </div>

        {/* view switch - Flashcards removed per request */}

        <div style={{ padding: "8px 10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--iv-surface)", borderRadius: T.radiusSm, border: `1px solid ${T.line}`, padding: "6px 10px" }}>
            <Search size={14} color={T.sub} />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search notes"
              style={{ border: "none", background: "transparent", outline: "none", fontSize: 13, flex: 1, color: T.ink }} />
          </div>
        </div>

        <div style={{ flex: 1, overflowX: "hidden", overflowY: "auto", padding: "2px 8px 10px" }}>
          {data.folders.map(f => {
            const open = openFolders[f.id];
            const inner = data.notes.filter(n => n.folderId === f.id && match(n));
            return (
              <div key={f.id}>
                <div style={S.row} className="rowhover" onClick={() => setOpenFolders(o => ({ ...o, [f.id]: !o[f.id] }))}>
                  {open ? <ChevronDown size={13} color={T.sub} /> : <ChevronRight size={13} color={T.sub} />}
                  <Folder size={14} color={T.accent} />
                  {renaming?.type === "folder" && renaming.id === f.id ? (
                    <input autoFocus defaultValue={f.name}
                      onClick={e => e.stopPropagation()}
                      onBlur={e => renameItem("folder", f.id, e.target.value)}
                      onKeyDown={e => e.key === "Enter" && renameItem("folder", f.id, e.target.value)}
                      style={{ flex: 1, fontSize: 13, border: `1px solid ${T.accent}`, borderRadius: T.radiusSm, padding: "1px 5px", outline: "none" }} />
                  ) : <span style={{ flex: 1, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</span>}
                  <span className="rowactions" style={{ display: "none", gap: 2 }}>
                    <FilePlus size={13} color={T.sub} onClick={e => { e.stopPropagation(); addNote(f.id); }} />
                    <Pencil size={13} color={T.sub} onClick={e => { e.stopPropagation(); setRenaming({ type: "folder", id: f.id }); }} />
                    <Trash2 size={13} color={T.sub} onClick={e => { e.stopPropagation(); deleteFolder(f.id); }} />
                  </span>
                </div>
                {open && inner.map(n => <NoteRow key={n.id} n={n} indent />)}
                {open && inner.length === 0 && <div style={{ paddingLeft: 32, fontSize: 12, color: "var(--iv-muted)", padding: "4px 0 4px 32px" }}>Empty folder</div>}
              </div>
            );
          })}
          {looseNotes.map(n => <NoteRow key={n.id} n={n} />)}
        </div>

        <div style={{ borderTop: `1px solid ${T.line}`, padding: 10, display: "flex", gap: 6 }}>
          <button onClick={addFolder} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "7px 0", borderRadius: T.radiusSm, border: `1px solid ${T.line}`, background: "var(--iv-surface)", cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: T.ink, height: 32 }}>
            <FolderPlus size={14} /> Folder
          </button>
          <button onClick={() => addNote(null)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "7px 0", borderRadius: T.radiusSm, border: "none", background: "var(--iv-btn)", color: "var(--iv-btn-fg)", cursor: "pointer", fontSize: 12.5, fontWeight: 600, height: 32 }}>
            <FilePlus size={14} /> Note
          </button>
        </div>
      </div>

      {/* Mobile backdrop overlay */}
      {sidebarOpen && (
        <div
          className="ivori-notes-backdrop"
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "color-mix(in srgb, var(--iv-band) 35%, transparent)",
            backdropFilter: "blur(2px)",
            zIndex: 250,
            display: "none"
          }}
        />
      )}

      {/* ============ MAIN ============ */}
      <div className="ivori-notes-main" style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, background: "var(--iv-bg)" }}>
        {/* Top Global Navigation Bar */}
        <div style={{ height: 48, background: "var(--iv-surface)", borderBottom: `1px solid ${T.line}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", flexShrink: 0, boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {!sidebarOpen && (
              <TB onClick={() => setSidebarOpen(true)} title="Expand notes directory">
                <PanelLeftOpen size={17} color={T.accent} />
              </TB>
            )}
            <div style={{ display: "flex", gap: 6, background: "var(--iv-bg)", padding: 3, borderRadius: 8 }}>
              <button
                onClick={() => setView("notes")}
                style={{
                  padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer",
                  background: view === "notes" ? "var(--iv-surface)" : "transparent",
                  color: view === "notes" ? T.ink : T.sub,
                  fontWeight: 600, fontSize: 12.5, display: "flex", alignItems: "center", gap: 5,
                  boxShadow: view === "notes" ? "0 1px 2px color-mix(in srgb, var(--iv-shadow) 6%, transparent)" : "none"
                }}
              >
                <FileText size={13} /> Notes
              </button>
              <button
                onClick={() => setView("library")}
                style={{
                  padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer",
                  background: view === "library" ? "var(--iv-surface)" : "transparent",
                  color: view === "library" ? T.ink : T.sub,
                  fontWeight: 600, fontSize: 12.5, display: "flex", alignItems: "center", gap: 5,
                  boxShadow: view === "library" ? "0 1px 2px color-mix(in srgb, var(--iv-shadow) 6%, transparent)" : "none"
                }}
              >
                <Sparkles size={13} color="var(--iv-accent)" /> Smart Library & Research
              </button>
              <button
                onClick={() => setView("cards")}
                style={{
                  padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer",
                  background: view === "cards" ? "var(--iv-raised)" : "transparent",
                  color: view === "cards" ? T.ink : T.sub,
                  fontWeight: 600, fontSize: 12.5, display: "flex", alignItems: "center", gap: 5,
                  boxShadow: view === "cards" ? "0 1px 2px color-mix(in srgb, var(--iv-shadow) 6%, transparent)" : "none"
                }}
              >
                <Layers size={13} /> Flashcards ({data.decks.length})
              </button>
            </div>
          </div>

          {view === "notes" && note && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                type="button"
                onClick={() => setSocraticOpen(true)}
                style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 8, border: `1px solid ${T.line}`, background: "var(--iv-surface)", color: "var(--iv-body)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                title="Launch Socratic Oral Defence Simulator"
              >
                <Award size={13} color="var(--iv-accent)" /> <span className="save-btn-text">Socratic Defence</span>
              </button>
              <button
                type="button"
                onClick={() => setAuditorOpen(true)}
                style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 8, border: `1px solid ${T.line}`, background: "var(--iv-surface)", color: "var(--iv-body)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                title="Run Dialectical Argument Auditor"
              >
                <ShieldCheck size={13} color="var(--iv-head)" /> <span className="save-btn-text">Argument Audit</span>
              </button>
              <button
                type="button"
                onClick={() => setAudioOpen(true)}
                style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 8, border: `1px solid ${T.line}`, background: "var(--iv-surface)", color: "var(--iv-body)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                title="Generate Dual-Persona Audio Synthesis"
              >
                <Volume2 size={13} color="var(--iv-accent)" /> <span className="save-btn-text">Audio Overview</span>
              </button>
              <button
                type="button"
                onClick={() => setSkeletonOpen(true)}
                style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 8, border: `1px solid ${T.line}`, background: "var(--iv-surface)", color: "var(--iv-body)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                title="Generate Academic Essay & Dissertation Skeleton"
              >
                <Layers size={13} color="var(--iv-accent)" /> <span className="save-btn-text">Essay Skeleton</span>
              </button>
              <button
                type="button"
                onClick={() => deckFromNote(note)}
                style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 8, border: `1px solid ${T.line}`, background: "var(--iv-surface)", color: T.accent, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                title="Generate AI flashcards from this note"
              >
                <Sparkles size={13} /> <span className="save-btn-text">Make Flashcards</span>
              </button>
            </div>
          )}
        </div>

        {view === "library" ? (
          <div style={{ flex: 1, overflowY: "auto" }}>
            <SmartLibrarySection onCreateNote={handleCreateNoteFromLibrary} />
          </div>
        ) : view === "notes" ? (
          !note ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, background: "var(--iv-bg)" }}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, color: T.sub, background: "var(--iv-bg)" }}>
                <BookOpen size={36} strokeWidth={1.4} />
                <div style={{ fontSize: 15 }}>Select a note, create a new one, or explore the Smart Library.</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => addNote(null)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 16, border: "none", background: "var(--iv-btn)", color: "var(--iv-btn-fg)", cursor: "pointer", fontSize: 13.5, fontWeight: 600 }}>
                    <Plus size={15} /> New note
                  </button>
                  <button onClick={() => setView("library")} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 16, border: `1px solid ${T.line}`, background: "var(--iv-surface)", color: T.ink, cursor: "pointer", fontSize: 13.5, fontWeight: 600 }}>
                    <Sparkles size={15} color="var(--iv-accent)" /> Browse Library
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, background: "var(--iv-bg)" }}>
            {/* toolbar */}
            <div style={{ height: 50, display: "flex", alignItems: "center", gap: 6, padding: "0 14px", borderBottom: `1px solid ${T.line}`, background: T.panel, flexWrap: "nowrap", position: "relative", boxSizing: "border-box" }}>
              {/* Scrollable toolbar items for mobile */}
              <div className="formatting-tools-scroll" style={{ display: "flex", alignItems: "center", gap: 4, overflowX: "auto", scrollbarWidth: "none", msOverflowStyle: "none" }}>
                <FontSizeDropdown onSelect={applyFontSize} />
                <TB onClick={() => exec("bold")} title="Bold (Ctrl+B)"><Bold size={15} /></TB>
                <TB onClick={() => exec("italic")} title="Italic (Ctrl+I)"><Italic size={15} /></TB>
                <TB onClick={() => exec("underline")} title="Underline (Ctrl+U)"><Underline size={15} /></TB>
                <TB onClick={() => exec("strikeThrough")} title="Strikethrough"><Strikethrough size={15} /></TB>
                <TB onClick={upcaseSelection} title="UPPERCASE selection"><CaseUpper size={16} /></TB>
                <div style={{ width: 1, height: 20, background: T.line, margin: "0 5px", flexShrink: 0 }} />
                <TB onClick={() => setColorMenu(colorMenu === "text" ? null : "text")} title="Text colour" active={colorMenu === "text"}><Type size={15} /></TB>
                <TB onClick={() => setColorMenu(colorMenu === "hl" ? null : "hl")} title="Highlight" active={colorMenu === "hl"}><Highlighter size={15} /></TB>
                <div style={{ width: 1, height: 20, background: T.line, margin: "0 5px", flexShrink: 0 }} />
                <TB onClick={() => exec("insertUnorderedList")} title="Bullet list"><List size={15} /></TB>
                <TB onClick={() => exec("insertOrderedList")} title="Numbered list"><ListOrdered size={15} /></TB>
                <TB onClick={() => exec("removeFormat")} title="Clear formatting"><Eraser size={15} /></TB>
              </div>

              <div style={{ flex: 1 }} />
              
              {/* Word Online / Google Docs style saving status & Save / Export actions */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, height: "100%", flexShrink: 0 }}>
                <span style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 12,
                  fontWeight: 600,
                  color: saveStatus === "saving" ? "var(--iv-accent)" : "var(--iv-success)",
                  background: saveStatus === "saving" ? "var(--iv-bg)" : "var(--iv-bg)",
                  padding: "4px 8px",
                  borderRadius: 8,
                  transition: "all 0.2s ease"
                }}>
                  {saveStatus === "saving" ? (
                    <>
                      <Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} />
                      <span className="save-status-text">Saving...</span>
                    </>
                  ) : (
                    <>
                      <Check size={13} />
                      <span className="save-status-text">Saved</span>
                    </>
                  )}
                </span>
                <button
                  type="button"
                  onClick={handleManualSave}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 9px", borderRadius: 8, border: `1px solid ${T.line}`, background: "var(--iv-surface)", color: T.ink, cursor: "pointer", fontSize: 12, fontWeight: 600, height: 30 }}
                  title="Save changes to your account profile"
                >
                  <Check size={13} color={T.accent} /> <span className="save-btn-text">Save</span>
                </button>
                <ExportDropdown note={note} />
              </div>

              {colorMenu && (
                <div style={{ position: "absolute", top: 44, left: 220, zIndex: 30, background: "var(--iv-surface)", border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 8px 24px color-mix(in srgb, var(--iv-shadow) 12%, transparent)", padding: 10, display: "flex", gap: 6, flexWrap: "wrap", width: 200 }}>
                  {(colorMenu === "text" ? TEXT_COLORS : HIGHLIGHTS).map(c => (
                    <button key={c.n} title={c.n}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => {
                        if (colorMenu === "text") {
                          exec("foreColor", c.v);
                        } else {
                          try {
                            exec("hiliteColor", c.v);
                          } catch {
                            exec("backColor", c.v);
                          }
                        }
                        setColorMenu(null);
                      }}
                      style={{ width: 26, height: 26, borderRadius: 7, cursor: "pointer", border: `1px solid ${T.line}`, background: c.v === "transparent" ? "var(--iv-surface)" : c.v, position: "relative" }}>
                      {c.v === "transparent" && <X size={13} color={T.sub} style={{ position: "absolute", inset: 0, margin: "auto" }} />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* title + editor */}
            <div style={{ flex: 1, overflowY: "auto" }} onClick={() => setColorMenu(null)}>
              <div className="editor-wrapper-padding" style={{ maxWidth: 760, margin: "0 auto", padding: "38px 40px 80px" }}>
                <input
                  value={note.title}
                  onChange={e => {
                    const v = e.target.value;
                    commit(d => ({ ...d, notes: d.notes.map(n => n.id === note.id ? { ...n, title: v } : n) }));
                  }}
                  placeholder="Untitled"
                  style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontFamily: 'var(--iv-font-head)', fontSize: 34, fontWeight: 700, letterSpacing: "-0.02em", color: T.ink, marginBottom: 14 }}
                />
                <div
                  ref={editorRef}
                  className="editor"
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleInput}
                  style={{ minHeight: 400, fontSize: 15.5, lineHeight: 1.65, color: T.ink }}
                />
              </div>
            </div>
          </div>
        )
      ) : (
        /* ============ FLASHCARDS ============ */
        <div style={{ flex: 1, overflowY: "auto" }}>
          <div style={{ maxWidth: 860, margin: "0 auto", padding: "38px 40px 80px" }}>
            <div style={{ fontFamily: 'var(--iv-font-head)', fontSize: 30, fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 4 }}>Flashcards</div>
            <div style={{ fontSize: 14, color: T.sub, marginBottom: 24 }}>Upload a PDF or turn any note into a study deck.</div>

            {/* upload */}
            <input ref={fileRef} type="file" accept="application/pdf" style={{ display: "none" }}
              onChange={e => { const f = e.target.files[0]; if (f) handlePdf(f); e.target.value = ""; }} />
            <div
              onClick={() => !genState.busy && fileRef.current.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f && f.type === "application/pdf") handlePdf(f); }}
              style={{ border: `1.5px dashed ${genState.busy ? T.accent : "var(--iv-line)"}`, borderRadius: 14, padding: "30px 20px", textAlign: "center", cursor: genState.busy ? "default" : "pointer", background: T.panel, marginBottom: 12 }}>
              {genState.busy ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 9, color: T.accent, fontSize: 14, fontWeight: 600 }}>
                  <Loader2 size={17} style={{ animation: "spin 1s linear infinite" }} /> {genState.msg}
                </div>
              ) : (
                <>
                  <Upload size={22} color={T.accent} style={{ marginBottom: 8 }} />
                  <div style={{ fontSize: 14.5, fontWeight: 600 }}>Drop a PDF here, or click to browse</div>
                  <div style={{ fontSize: 12.5, color: T.sub, marginTop: 3 }}>Claude reads it and writes a deck of flashcards for you.</div>
                </>
              )}
            </div>
            {genState.err && <div style={{ background: "var(--iv-bg)", color: "var(--iv-danger)", borderRadius: 9, padding: "9px 13px", fontSize: 13, marginBottom: 12 }}>{genState.err}</div>}

            {/* decks */}
            {data.decks.length === 0 ? (
              <div style={{ color: T.sub, fontSize: 13.5, padding: "18px 4px" }}>No decks yet. Your generated decks will appear here.</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14, marginTop: 18 }}>
                {data.decks.map(deck => {
                  const known = deck.cards.filter(c => c.known).length;
                  return (
                    <div key={deck.id} style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 14, padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <Layers size={17} color={T.accent} style={{ marginTop: 2 }} />
                        <div style={{ flex: 1, fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>{deck.name}</div>
                        <Trash2 size={14} color={T.sub} style={{ cursor: "pointer" }} onClick={() => deleteDeck(deck.id)} />
                      </div>
                      <div style={{ fontSize: 12.5, color: T.sub }}>{deck.cards.length} cards · {known} learned</div>
                      <div style={{ height: 5, borderRadius: 3, background: "var(--iv-bg)", overflow: "hidden" }}>
                        <div style={{ width: `${deck.cards.length ? (known / deck.cards.length) * 100 : 0}%`, height: "100%", background: "var(--iv-btn)", borderRadius: 3 }} />
                      </div>
                      <button onClick={() => setStudyDeck({ id: deck.id, order: deck.cards.map((_, i) => i), idx: 0, flipped: false })}
                        style={{ marginTop: 2, padding: "8px 0", borderRadius: 9, border: "none", background: "var(--iv-btn)", color: "var(--iv-btn-fg)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                        Study deck
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      </div>

      {/* ============ STUDY MODAL ============ */}
      {studyDeck && (() => {
        const deck = data.decks.find(d => d.id === studyDeck.id);
        if (!deck) { setStudyDeck(null); return null; }
        const total = studyDeck.order.length;
        const done = studyDeck.idx >= total;
        const card = done ? null : deck.cards[studyDeck.order[studyDeck.idx]];
        const mark = (known) => {
          commit(d => ({
            ...d, decks: d.decks.map(k => k.id !== deck.id ? k : {
              ...k, cards: k.cards.map((c, i) => i === studyDeck.order[studyDeck.idx] ? { ...c, known } : c)
            })
          }));
          setStudyDeck(s => ({ ...s, idx: s.idx + 1, flipped: false }));
        };
        const shuffle = () => {
          const order = [...studyDeck.order].sort(() => Math.random() - 0.5);
          setStudyDeck(s => ({ ...s, order, idx: 0, flipped: false }));
        };
        return (
          <div style={{ position: "fixed", inset: 0, background: "color-mix(in srgb, var(--iv-band) 55%, transparent)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ background: T.bg, borderRadius: 20, width: "100%", maxWidth: 620, padding: "22px 26px 26px", position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>{deck.name}</div>
                <TB onClick={shuffle} title="Shuffle"><Shuffle size={15} /></TB>
                <TB onClick={() => setStudyDeck(s => ({ ...s, idx: 0, flipped: false }))} title="Restart"><RotateCcw size={15} /></TB>
                <TB onClick={() => setStudyDeck(null)} title="Close"><X size={16} /></TB>
              </div>
              <div style={{ height: 5, borderRadius: 3, background: "var(--iv-bg)", marginBottom: 18, overflow: "hidden" }}>
                <div style={{ width: `${(Math.min(studyDeck.idx, total) / total) * 100}%`, height: "100%", background: "var(--iv-btn)", transition: "width .3s" }} />
              </div>

              {done ? (
                <div style={{ textAlign: "center", padding: "50px 0" }}>
                  <div style={{ fontFamily: 'var(--iv-font-head)', fontSize: 26, fontWeight: 700, marginBottom: 8 }}>Deck complete</div>
                  <div style={{ color: T.sub, fontSize: 14, marginBottom: 20 }}>
                    {deck.cards.filter(c => c.known).length} of {deck.cards.length} marked as learned.
                  </div>
                  <button onClick={() => setStudyDeck(s => ({ ...s, idx: 0, flipped: false }))}
                    style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "var(--iv-btn)", color: "var(--iv-btn-fg)", fontWeight: 600, fontSize: 13.5, cursor: "pointer" }}>
                    Study again
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ perspective: 1200, height: 300, cursor: "pointer" }} onClick={() => setStudyDeck(s => ({ ...s, flipped: !s.flipped }))}>
                    <div className="flip" style={{ position: "relative", width: "100%", height: "100%", transform: studyDeck.flipped ? "rotateY(180deg)" : "none" }}>
                      <div className="face" style={{ background: T.card, color: "var(--iv-on-band)" }}>
                        <div style={{ fontSize: 11, letterSpacing: "0.12em", opacity: .55, marginBottom: 14, textTransform: "uppercase" }}>Question · {studyDeck.idx + 1} / {total}</div>
                        <div style={{ fontFamily: 'var(--iv-font-body)', fontSize: 21, lineHeight: 1.4 }}>{card.q}</div>
                        <div style={{ fontSize: 12, opacity: .5, marginTop: 22 }}>Tap to reveal</div>
                      </div>
                      <div className="face" style={{ background: T.cardBack, color: T.ink, transform: "rotateY(180deg)", border: `1px solid ${T.line}` }}>
                        <div style={{ fontSize: 11, letterSpacing: "0.12em", color: T.sub, marginBottom: 14, textTransform: "uppercase" }}>Answer</div>
                        <div style={{ fontSize: 18, lineHeight: 1.5 }}>{card.a}</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                    <button onClick={() => mark(false)} style={{ flex: 1, padding: "12px 0", borderRadius: 11, border: `1px solid ${T.line}`, background: "var(--iv-surface)", color: T.ink, fontWeight: 600, fontSize: 13.5, cursor: "pointer" }}>
                      Still learning
                    </button>
                    <button onClick={() => mark(true)} style={{ flex: 1, padding: "12px 0", borderRadius: 11, border: "none", background: "var(--iv-btn)", color: "var(--iv-btn-fg)", fontWeight: 600, fontSize: 13.5, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
                      <Check size={15} /> Got it
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {socraticOpen && note && (
        <SocraticExaminerModal note={note} onClose={() => setSocraticOpen(false)} />
      )}
      {auditorOpen && note && (
        <ArgumentAuditorModal note={note} onClose={() => setAuditorOpen(false)} />
      )}
      {audioOpen && note && (
        <AudioOverviewModal note={note} onClose={() => setAudioOpen(false)} />
      )}
      {skeletonOpen && note && (
        <EssaySkeletonModal
          note={note}
          onClose={() => setSkeletonOpen(false)}
          onInsertIntoNote={handleAppendSkeleton}
        />
      )}
    </div>
  );
}
